import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CircleIcon } from './icons/AdminIcons';
import './CustomSelect.css';

interface SelectOption {
  value: string;
  label: string;
  color?: string;
  disabled?: boolean;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  /** Si hay placeholder y `value` está vacío, muestra el texto gris sin forzar la primera opción. */
  placeholder?: string;
  className?: string;
  optionsClassName?: string;
  onClick?: (e: React.MouseEvent) => void;
  id?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  placeholder,
  className = '',
  optionsClassName = '',
  onClick,
  id,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const [usePortal, setUsePortal] = useState(false);
  const [portalPosition, setPortalPosition] = useState<{ top: number; left: number; width: number; inputTop?: number; inputBottom?: number }>({ top: 0, left: 0, width: 0 });
  const [openUpward, setOpenUpward] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node) &&
          optionsRef.current && !optionsRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setOpenUpward(false);
        setUsePortal(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Cerrar el desplegable al hacer scroll en la página (no al scroll dentro del propio desplegable)
  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (e: Event) => {
      const target = e.target as Node;
      if (optionsRef.current?.contains(target) || selectRef.current?.contains(target)) return;
      setIsOpen(false);
      setOpenUpward(false);
      setUsePortal(false);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  // Proteger contra opciones vacías o undefined (después de los Hooks)
  if (!options || options.length === 0) {
    return (
      <div className={`custom-select custom-select-selected ${className}`}>
        <span className="custom-select-text">Cargando...</span>
      </div>
    );
  }

  const matched = options.find((opt) => opt.value === value);
  const showAsPlaceholder =
    Boolean(placeholder) && (value === '' || value === undefined || value === null) && !matched;
  const selectedOption = matched ?? (!showAsPlaceholder ? options[0] : undefined);

  if (!selectedOption && !showAsPlaceholder) {
    return (
      <div id={id} className={`custom-select custom-select-selected ${className}`}>
        <span className="custom-select-text">Sin opciones</span>
      </div>
    );
  }

  const handleSelect = (optionValue: string) => {
    const opt = options.find((o) => o.value === optionValue);
    if (opt?.disabled) return;
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleToggle = (e: React.MouseEvent) => {
    if (onClick) onClick(e);
    const newIsOpen = !isOpen;
    
    if (newIsOpen && selectRef.current) {
      // Calcular posición ANTES de cambiar el estado
      const rect = selectRef.current.getBoundingClientRect();
      const optionsHeight = 200;
      const spaceBelow = window.innerHeight - rect.bottom;
      const shouldOpenUp = spaceBelow < optionsHeight;
      
      // Usar portal si está en columna fija de tabla O dentro de tarjeta de contacto/empresa
      // (evita que el dropdown quede tapado por la tarjeta siguiente al hacer hover)
      const cell = selectRef.current.closest('td');
      const insideCard = selectRef.current.closest('.admin-crm-contact-card');
      const insideNetModal = selectRef.current.closest('.net-modal');
      let shouldUsePortal = false;
      let portalPos = { top: 0, left: 0, width: 0, inputTop: rect.top, inputBottom: rect.bottom };
      
      if (cell) {
        const row = cell.parentElement;
        const insideNegociosTable = cell.closest('.admin-negocios-table');
        if (row) {
          const cellIndex = Array.from(row.children).indexOf(cell);
          // Portal en columnas 0 y 1 de cualquier tabla, o en cualquier celda de la tabla Negocios (evita que el desplegable quede tapado)
          if (insideNegociosTable || cellIndex === 0 || cellIndex === 1) {
            shouldUsePortal = true;
            portalPos = {
              top: shouldOpenUp ? Math.max(0, rect.top - optionsHeight - 4) : rect.bottom + 4,
              left: rect.left,
              width: rect.width,
              inputTop: rect.top,
              inputBottom: rect.bottom
            };
          }
        }
      } else if (insideCard || insideNetModal) {
        shouldUsePortal = true;
        portalPos = {
          top: shouldOpenUp ? 0 : rect.bottom + 4,
          left: rect.left,
          width: rect.width,
          inputTop: rect.top,
          inputBottom: rect.bottom
        };
      }
      
      // Actualizar todos los estados de forma síncrona
      setOpenUpward(shouldOpenUp);
      setUsePortal(shouldUsePortal);
      if (shouldUsePortal) {
        setPortalPosition(portalPos);
      }
      setIsOpen(true);
    } else {
      setIsOpen(false);
      setOpenUpward(false);
      setUsePortal(false);
    }
  };

  const triggerGreyStyle = showAsPlaceholder || (!matched && (value === '' || value === undefined || value === null));

  return (
    <div
      id={id}
      className={`custom-select custom-select-selected ${className} ${isOpen ? 'open' : ''}`}
      data-value={value}
      ref={selectRef}
      onClick={handleToggle}
    >
      {selectedOption?.color && (
        <CircleIcon width={12} height={12} color={selectedOption.color} className="custom-select-icon" />
      )}
      <span className={`custom-select-text ${triggerGreyStyle ? 'custom-select-placeholder' : ''}`}>
        {showAsPlaceholder ? placeholder : selectedOption!.label}
      </span>
      {isOpen && (() => {
        // Calcular valores en el render para asegurar que siempre estén actualizados
        let calculatedUsePortal = usePortal;
        let calculatedOpenUpward = openUpward;
        let calculatedPortalPos = portalPosition;
        
        if (selectRef.current) {
          const rect = selectRef.current.getBoundingClientRect();
          const optionsHeight = 200;
          const spaceBelow = window.innerHeight - rect.bottom;
          calculatedOpenUpward = spaceBelow < optionsHeight;
          
          const cell = selectRef.current.closest('td');
          const insideCard = selectRef.current.closest('.admin-crm-contact-card');
          const insideNetModal = selectRef.current.closest('.net-modal');
          const insideNegociosTable = cell ? cell.closest('.admin-negocios-table') : null;
          if (cell) {
            const row = cell.parentElement;
            if (row) {
              const cellIndex = Array.from(row.children).indexOf(cell);
              if (insideNegociosTable || cellIndex === 0 || cellIndex === 1) {
                calculatedUsePortal = true;
                calculatedPortalPos = {
                  top: calculatedOpenUpward ? 0 : rect.bottom + 4,
                  left: rect.left,
                  width: rect.width,
                  inputTop: rect.top,
                  inputBottom: rect.bottom
                };
              } else {
                calculatedUsePortal = false;
              }
            }
          } else if (insideCard || insideNetModal) {
            calculatedUsePortal = true;
            calculatedPortalPos = {
              top: calculatedOpenUpward ? 0 : rect.bottom + 4,
              left: rect.left,
              width: rect.width,
              inputTop: rect.top,
              inputBottom: rect.bottom
            };
          } else {
            calculatedUsePortal = false;
          }
        }
        
        const optionsList = options.map((option) => (
          <div
            key={option.value}
            className={`custom-select-option ${value === option.value ? 'selected' : ''} ${option.disabled ? 'disabled' : ''}`}
            onClick={() => handleSelect(option.value)}
          >
            {option.color && (
              <CircleIcon width={12} height={12} color={option.color} className="custom-select-option-icon" />
            )}
            <span>{option.label}</span>
          </div>
        ));
        
        if (calculatedUsePortal) {
          const portalStyle: React.CSSProperties = {
            position: 'fixed',
            left: `${calculatedPortalPos.left}px`,
            width: `${calculatedPortalPos.width}px`,
            minWidth: `${calculatedPortalPos.width}px`,
            maxWidth: `${calculatedPortalPos.width}px`,
            zIndex: 99999
          };
          
          if (calculatedOpenUpward) {
            // Cuando se abre hacia arriba, posicionar justo arriba del input
            const inputTop = calculatedPortalPos.inputTop || calculatedPortalPos.top;
            portalStyle.top = `${inputTop - 4}px`;
            portalStyle.bottom = 'auto';
            portalStyle.transform = 'translateY(-100%)';
          } else {
            portalStyle.top = `${calculatedPortalPos.top}px`;
          }
          
          return createPortal(
            <div 
              ref={optionsRef}
              className={`custom-select-options custom-select-options-portal ${optionsClassName}`.trim()}
              style={portalStyle}
            >
              {optionsList}
            </div>,
            document.body
          );
        }
        
        return (
          <div 
            ref={optionsRef}
            className={`custom-select-options ${calculatedOpenUpward ? 'custom-select-options-upward' : ''} ${optionsClassName}`.trim()}
            style={calculatedOpenUpward ? {
              position: 'absolute',
              bottom: '100%',
              top: 'auto',
              left: '0',
              right: '0',
              marginTop: '0',
              marginBottom: '4px',
              zIndex: 1000
            } : undefined}
          >
            {optionsList}
          </div>
        );
      })()}
    </div>
  );
};

const ChevronDownIcon: React.FC<{ width: number; height: number; className?: string }> = ({ width, height, className }) => (
  <svg width={width} height={height} viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

export default CustomSelect;
